import { IScheduler } from "../../../utils/scheduler";
import { ITaskOptions, Service } from "../../serviceBase";
import { DAYS, HOURS } from "../../../constants";
import * as logger from "../../../utils/logger";
import { DriveStore } from "../../../store/reducers";
import Api from "../../../services/api";
import { handleApi } from "../../../utils/error";
import { cleanUpSafetyDriverInsightData } from "../helpers/driverSafetyDataProcessor";
import { DriverSafetyActions } from "../store/actions";
import { ServerFlags } from "../../../store/data/types";
import { UserService } from "../../../../app/utils/users";
import { SecurityService } from "../../../../app/utils/security";

export default class ScheduledDriverSafetyService extends Service {

    options: ITaskOptions = {
        name: "@scheduledDriverSafetyService",
        msInterval: 12 * HOURS,
        hasDelayAfterStart: true,
        registerOnceForAllUsers: false,
        onDemand: false,
        startingInterval: window.DRIVE_DEBUG?.STARTING_INTERVAL || Math.floor(Math.random() * (10 - 1 + 1) + 1) * 1000,
        delayByInterval: false
    };

    constructor(
        private readonly store: DriveStore,
        private readonly api: Api,
        protected readonly scheduler: IScheduler,
        private readonly userService: UserService,
        private readonly securityService: SecurityService
    ) {
        super(scheduler);
        this.userName = this.api.getUser()?.name;
        this.registerTask(this.check.bind(this), this.options, this.userName);
    }

    check() {
        let currentUser = this.api.getUser();
        if (!this.canOpen(currentUser)){
            return;
        }

        logger.log(logger.Source.SERVICES, "ScheduledDriverSafetyService is running");

        let currentTimeInMS = Date.now();
        let now = new Date(currentTimeInMS);
        let oneYearAgo = new Date(currentTimeInMS - 366 * DAYS);

        if (currentUser && currentUser.id) {
            const baseFields = [
                "id",
                "driver",
                "crashProbabilityKm",
                "crashProbabilityMile",
                "crashProbabilityBenchmarkKm",
                "crashProbabilityBenchmarkMile",
                "harshAccelerationRange",
                "harshAccelerationRank",
                "harshBrakingRange",
                "harshBrakingRank",
                "harshCorneringRange",
                "harshCorneringRank",
                "speedingRange",
                "speedingRank",
                "observationDate",
                "crashProbabilityBestInClassKm",
                "crashProbabilityBestInClassMile",
                "crashProbabilityRange",
                "crashProbabilityRank",
                "majorVersion"
            ];

            baseFields.push("tailgatingRank", "tailgatingRange", "metadata");

            return this.api
                .callPromise("Get", {
                    typeName: "SafetyDriverInsight",
                    search: {
                        fromDate: oneYearAgo,
                        toDate: now,
                        userSearch: {
                            id: currentUser?.id
                        }
                    },
                    sort: {
                        sortBy: "ObservationDate",
                        sortDirection: "asc"
                    },
                    propertySelector: {
                        fields: baseFields,
                        isIncluded: true
                    }
                })
                .then(results => {
                    if (!results.length) {
                        logger.log(logger.Source.SERVICES, "No SafetyDriverInsight data available for user - clearing the driverSafety data store", currentUser);
                        this.store.dispatch(DriverSafetyActions.clearDriverSafetyInsight(currentUser?.id));
                    } else {
                        let processedData = cleanUpSafetyDriverInsightData(results);
                        this.store.dispatch(DriverSafetyActions.setDriverSafetyInsight({ userId: currentUser?.id, driverSafety: processedData }));
                        logger.log(logger.Source.SERVICES, "ScheduledDriverSafetyService driverSafety store updated", currentUser);
                    }
                    return Promise.resolve();
                })
                .catch(handleApi);
        }
        return;
    }

    destroy(): void {
        super.destroy();
    }
    //FedRAMP check
    private checkServerFlagEnabled(shouldThrow = true, serverFlag: ServerFlags = this.store.getState().data.serverFlags): boolean {
        const isServerFlagEnabled = serverFlag !== ServerFlags.None;
        if (!isServerFlagEnabled && shouldThrow) {
            throw new Error("The target server doesn't allow Driver Safety.");
        }
        return isServerFlagEnabled;
    }

    private canOpen(user: IUser | null): boolean {
        return this.checkServerFlagEnabled(false)
            && this.securityService.can("viewDriverSafety", user?.id)
            && this.userService.isDriverUsingUI(user?.name || "");
    }
}
